import React, { useState, useEffect } from 'react';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../../hooks/useUser';
import { isPublicRoute } from '../../utils/routeUtils';

// Get chatbot URL from environment variable or use fallback
const CHATBOT_URL = import.meta.env.VITE_CHATBOT_URL || 'https://apfbeipepbotdevwesteu-e6g5ejgqc3bjezep.westeurope-01.azurewebsites.net/popout';

/**
 * NOTE: Configure the chatbot URL in your .env files:
 * VITE_CHATBOT_URL=https://your-chatbot-url.com/popout?sections=e09-ibrs&submodules=e09-despatches
 * 
 * NOTE: If you see "refused to connect" error, the chatbot server needs to be configured 
 * to allow iframe embedding by setting proper headers:
 * - Content-Security-Policy: frame-ancestors 'self' http://localhost:3000 https://your-domain.com
 * - Remove or modify X-Frame-Options header
 * 
 * Contact the backend team to configure these headers on the chatbot server.
 */

const ChatbotPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Read user data from cache only - UserInformationProvider handles fetching.
  // Mirrors RightHeader's auth gate so the chatbot (and its iframe) only mounts
  // once there's a valid session; otherwise the bot fires API calls pre-auth
  // and gets a 401 (e.g. on the login screen).
  const { data: userInfo, isError: userError } = useUser(false);
  const isAuthenticated = !!userInfo && !userError;

  // Close panel on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Prevent body scroll when mobile panel is open
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isOpen]);

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  // Don't render the chatbot on public/auth routes or before the user is authenticated
  if (isPublicRoute(location.pathname) || !isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <IconButton
          onClick={togglePanel}
          data-testid="chatbot-toggle-button"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 60,
            height: 60,
            backgroundColor: 'rgb(13 71 161 / 0.89)',
            color: '#ffffff',
            boxShadow: '0 6px 20px rgba(13, 71, 161, 0.55), 0 2px 6px rgba(0, 0, 0, 0.3)',
            overflow: 'visible',
            zIndex: 1299, // Below modals (1300) but above most content
            border: '2px solid rgba(255, 255, 255, 0.25)',
            '@keyframes chatbotPulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: '0 6px 20px rgba(13, 71, 161, 0.55), 0 2px 6px rgba(0, 0, 0, 0.3)',
              },
              '50%': {
                transform: 'scale(1.12)',
                boxShadow: '0 10px 30px rgba(13, 71, 161, 0.7), 0 4px 12px rgba(0, 0, 0, 0.35)',
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: '0 6px 20px rgba(13, 71, 161, 0.55), 0 2px 6px rgba(0, 0, 0, 0.3)',
              },
            },
            '&:hover': {
              backgroundColor: 'rgb(10 56 128 / 0.86)',
              animation: 'chatbotPulse 0.7s ease-in-out infinite',
            },
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          }}
        >
          <ChatIcon sx={{ fontSize: 30 }} />
        </IconButton>
      )}

      {/* Chatbot Panel */}
      {isOpen && (
        <Box
          data-testid="chatbot-panel"
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: isMobile ? '100%' : '25%',
            minWidth: isMobile ? 'auto' : '400px',
            maxWidth: isMobile ? '100%' : '600px',
            backgroundColor: 'white',
            boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.1)',
            zIndex: 1299,
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideIn 0.3s ease-out',
            '@keyframes slideIn': {
              from: {
                transform: 'translateX(100%)',
              },
              to: {
                transform: 'translateX(0)',
              },
            },
          }}
        >
          {/* Header with Close Button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 2,
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#1976d2',
              color: 'white',
            }}
          >
            <Box sx={{ fontSize: '18px', fontWeight: 600 }}>iPEP Chatbot</Box>
            <IconButton
              onClick={togglePanel}
              data-testid="chatbot-close-button"
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Iframe Container */}
          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <iframe
              src={CHATBOT_URL}
              title="iPEP Chatbot"
              data-testid="chatbot-iframe"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="microphone; camera"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
            />
          </Box>
        </Box>
      )}

      {/* Overlay for mobile (optional - closes panel when clicking outside) */}
      {isOpen && isMobile && (
        <Box
          data-testid="chatbot-overlay"
          onClick={togglePanel}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1298,
            animation: 'fadeIn 0.3s ease-out',
            '@keyframes fadeIn': {
              from: {
                opacity: 0,
              },
              to: {
                opacity: 1,
              },
            },
          }}
        />
      )}
    </>
  );
};

export default ChatbotPanel;
